/**
 * w-projectId
 * w-projectId-feature
 * w-projectId-feature-featureId
 *
 *
 * r-projectId
 * r-projectId-feature
 * r-projectId-feature-featureId
 *
 *
 *
 *
 *
 *
 */
export var Feature;
(function (Feature) {
    Feature["SCHEDULES"] = "schedule";
    Feature["CONTRACTS"] = "contracts";
    Feature["RFIS"] = "rfis";
    Feature["SUBMITTALS"] = "submittals";
    Feature["FILES"] = "files";
    Feature["FINANCIALS"] = "financials";
})(Feature || (Feature = {}));
export default class RoleUtils {
    static ADMIN_ROLES_ID = 'admin';
    static ADMIN_GROUP_ID = 'admin';
    static PROJECT_DB_NAME = /^p-(\d+|demo)(?:-.*)?$/;
    projectId;
    constructor(projectId) {
        this.projectId = projectId;
    }
    get anyoneReadWrite() {
        return [`p-${this.projectId}-read`, `p-${this.projectId}-basic`]; // Databases such as feed, tags, files that can be written by all users.
    }
    get anyoneReadAdminWrite() {
        return [`p-${this.projectId}-read`, `p-${this.projectId}-basic`, `p-${this.projectId}-write`]; // Databases such as feed, tags, files that can be written by all users.
    }
    get peopleRoles() {
        return [...this.anyoneReadAdminWrite, `p-${this.projectId}-people-write`];
    }
    /** Same access model as tags/photos/calendars: any basic project member can read, only project admins can write. */
    get codesRoles() {
        return this.anyoneReadAdminWrite;
    }
    get featureBaseRoles() {
        return [`p-${this.projectId}-read`, `p-${this.projectId}-write`]; // must not include -basic
    }
    get filesRoles() {
        return [...this.featureBaseRoles, `p-${this.projectId}-files-read`, `p-${this.projectId}-files-write`];
    }
    /**
     * Roles allowed on schedule-related databases.
     * Names match Feature.SCHEDULES (`schedule-read` / `schedule-write`).
     */
    get scheduleRoles() {
        return [
            ...this.featureBaseRoles,
            `p-${this.projectId}-schedule-read`,
            `p-${this.projectId}-schedule-write`,
        ];
    }
    get scheduleFilterRoles() {
        return [`p-${this.projectId}-read`, `p-${this.projectId}-schedule-read`]; // no write restriction on schedule filters
    }
    /**
     * Roles for resources + schedule-worklogs DBs.
     * Schedule is the parent of worklogs/estimates: schedule roles are included.
     * Canonical worklog roles are `schedule-worklogs-*`; legacy `worklogs-*` kept for older DBs.
     */
    get scheduleWorklogsRoles() {
        return [
            ...this.scheduleRoles,
            `p-${this.projectId}-schedule-worklogs-read`,
            `p-${this.projectId}-schedule-worklogs-write`,
            `p-${this.projectId}-worklogs-read`,
            `p-${this.projectId}-worklogs-write`,
        ];
    }
    /**
     * Roles for the shared `p-{id}-resources` database.
     * Readable by schedule (parent of estimates/worklogs) and financials; legacy names included.
     */
    get resourcesRoles() {
        return [
            ...this.scheduleWorklogsRoles,
            `p-${this.projectId}-financials-read`,
            `p-${this.projectId}-financials-write`,
        ];
    }
    get dailylogsRoles() {
        return [...this.featureBaseRoles, `p-${this.projectId}-dailylogs-write`, `p-${this.projectId}-dailylogs-read`];
    }
    get rfiRoles() {
        return [...this.featureBaseRoles, `p-${this.projectId}-rfis-read`, `p-${this.projectId}-rfis-write`];
    }
    get submittalRoles() {
        return [...this.featureBaseRoles, `p-${this.projectId}-submittals-read`, `p-${this.projectId}-submittals-write`];
    }
    get contractRoles() {
        return [...this.featureBaseRoles, `p-${this.projectId}-contracts-read`, `p-${this.projectId}-contracts-write`];
    }
    get financialsRoles() {
        return [...this.featureBaseRoles, `p-${this.projectId}-financials-read`, `p-${this.projectId}-financials-write`];
    }
    static getBasicRole(projectId) {
        return `p-${projectId}-basic`;
    }
    /** Project-wide read role used by the CouchDB proxy (`hasProjectReadRole`). */
    /** I am thinking to deprecate this in favour of users choosing roles */
    static getProjectReadRole(projectId) {
        return `p-${projectId}-read`;
    }
    static getProjectWriteRole(projectId) {
        return `p-${projectId}-write`;
    }
    static isProjectRole(role, projectId) {
        return role === `p-${projectId}` || role.startsWith(`p-${projectId}-`);
    }
    static getTopLevelPermissions(projectId) {
        return [
            {
                id: 'admin',
                name: 'Entire Project',
                icon: 'settings-outline',
                readRole: `p-${projectId}-read`,
                writeRole: `p-${projectId}-write`,
            },
            {
                id: 'users',
                name: 'Users & Roles',
                icon: 'people-outline',
                readRole: `p-${projectId}-users-read`, /* This role does not actually do anything. */
                writeRole: `p-${projectId}-users-write`,
            },
            {
                id: 'schedule',
                name: 'Schedule',
                icon: 'calendar-outline',
                readRole: `p-${projectId}-schedule-read`,
                writeRole: `p-${projectId}-schedule-write`,
            },
            {
                id: 'worklogs',
                name: 'Worklogs (requires Schedule)',
                icon: 'time-outline',
                readRole: `p-${projectId}-schedule-worklogs-read`,
                writeRole: `p-${projectId}-schedule-worklogs-write`,
            },
            {
                id: 'digest',
                name: 'Sign Daily Logs',
                icon: 'pencil-outline',
                readRole: `p-${projectId}-dailylogs-read`,
                writeRole: `p-${projectId}-dailylogs-write`,
            },
            {
                id: 'drawings',
                name: 'Drawings',
                icon: '/assets/icon/drawings-icon.svg',
                readRole: `p-${projectId}-drawings-read`,
                writeRole: `p-${projectId}-drawings-write`,
            },
            {
                id: 'specifications',
                name: 'Specifications',
                icon: '/assets/icon/specifications-icon.svg',
                readRole: `p-${projectId}-specifications-read`,
                writeRole: `p-${projectId}-specifications-write`,
            },
            {
                id: 'scrapbooks',
                name: 'Scrapbooks',
                icon: '/assets/icon/scrapbook-icon.svg',
                readRole: `p-${projectId}-scrapbooks-read`,
                writeRole: `p-${projectId}-scrapbooks-write`,
            },
            {
                id: 'rfis',
                name: 'RFIs',
                icon: 'help-circle-outline',
                readRole: `p-${projectId}-rfis-read`,
                writeRole: `p-${projectId}-rfis-write`,
            },
            {
                id: 'submittals',
                name: 'Submittals',
                icon: 'checkmark-circle-outline',
                readRole: `p-${projectId}-submittals-read`,
                writeRole: `p-${projectId}-submittals-write`,
            },
            {
                id: 'contracts',
                name: 'Contracts & Change Orders',
                icon: 'document-lock-outline',
                readRole: `p-${projectId}-contracts-read`,
                writeRole: `p-${projectId}-contracts-write`,
            },
        ];
    }
    /** Feature-level read roles from `getTopLevelPermissions` (one `-read` per feature). */
    static getTopLevelReadRoles(projectId) {
        return this.getTopLevelPermissions(projectId).map((permission) => permission.readRole);
    }
    /** Feature-level write roles from `getTopLevelPermissions` (one `-write` per feature that allows it). */
    static getTopLevelWriteRoles(projectId) {
        return this.getTopLevelPermissions(projectId)
            .map((permission) => permission.writeRole)
            .filter((role) => Boolean(role));
    }
    /** Default "View Project" roles: project read only — it implies every feature read. */
    static getViewerRoles(projectId) {
        return [this.getProjectReadRole(projectId)];
    }
    /** Default "Administrator" roles: project write only — it implies every feature write. */
    static getAdministratorRoles(projectId) {
        return [this.getProjectWriteRole(projectId)];
    }
    static isProjectWideViewerRoles(projectId, roles) {
        return this.hasProjectReadRole(projectId, roles) && !this.hasProjectWriteRole(projectId, roles);
    }
    static getRoleGroups(projectId) {
        return [
            {
                icon: 'business-outline',
                title: 'Project Wide Access',
                roles: [
                    { name: 'Can View Entire Project', role: `p-${projectId}-read` },
                    { name: 'Can make changes anywhere in the project', role: `p-${projectId}-write` },
                ],
            },
            {
                icon: 'people',
                title: 'Manage People',
                roles: [{ name: 'Can invite users to the project and update access', role: `p-${projectId}-people-write` }],
            },
            {
                icon: 'calendar',
                title: 'Schedule',
                roles: [
                    { name: 'Can view the Schedule', role: `p-${projectId}-schedule-read` },
                    { name: 'Can upload and edit schedules', role: `p-${projectId}-schedule-write` },
                ],
            },
            {
                icon: 'document',
                title: 'Files',
                roles: [
                    { name: 'Can view all Files', role: `p-${projectId}-files-read` },
                    { name: 'Can upload Files', role: `p-${projectId}-files-write` },
                ],
            },
            {
                icon: 'help-circle',
                title: 'RFIs',
                roles: [
                    { name: 'Can view RFIs', role: `p-${projectId}-rfis-read` },
                    { name: 'Can create and edit RFIS', role: `p-${projectId}-rfis-write` },
                ],
            },
            {
                icon: 'document-text',
                title: 'Submittals',
                roles: [
                    { name: 'Can view Submittals', role: `p-${projectId}-submittals-read` },
                    { name: 'Can create and edit Submittals', role: `p-${projectId}-submittals-write` },
                ],
            },
            {
                icon: 'document',
                title: 'Contracts',
                roles: [
                    { name: 'Can view Contracts', role: `p-${projectId}-contracts-read` },
                    { name: 'Can create and edit Contracts', role: `p-${projectId}-contracts-write` },
                ],
            },
            {
                icon: 'cash',
                title: 'Financials',
                roles: [
                    { name: 'Can view Financials', role: `p-${projectId}-financials-read` },
                    { name: 'Can create and edit Financials and resources', role: `p-${projectId}-financials-write` },
                ],
            },
            /*This should be done under p-projectId-schedule-write
            {
              icon: 'settings',
              title: 'Schedule Settings',
              roles: [
                { name: 'Can view Schedule Settings', role: `p-${projectId}-type-read` },
                { name: 'Can create and edit schedule settings', role: `p-${projectId}-type-write` },
              ],
            }
            This will change massively with budget, and I suspect the role will be p-projectId-budget-write for estimates.
      
            To give write access to log hours against a specific schedule task, we will need a specific db
            e.g p-projectId-worklogs
      
            Access can be given using doc level access,
            e.g p-projectId-worklogs-taskId-write
            ,*/
            {
                icon: 'document-text',
                title: 'Daily Work Logs',
                roles: [
                    { name: 'Can view Estimates and Daily Logs', role: `p-${projectId}-worklogs-read` },
                    { name: 'Can create and edit estimates and daily logs', role: `p-${projectId}-worklogs-write` },
                ],
            },
            {
                icon: 'document-text',
                title: 'Digest Signing',
                roles: [{ name: 'Can sign the daily digest', role: `p-${projectId}-digest-write` }],
            },
        ];
    }
    static getBasicRoles(projectId) {
        return [`p-${projectId}-read`, `p-${projectId}-write`, `p-${projectId}-basic`];
    }
    static canReadWriteBasic(projectId, roles) {
        return this.getBasicRoles(projectId).some((role) => roles.includes(role));
    }
    static canWrite(projectId, feature, featureId, roles) {
        if (projectId && feature && featureId && this.hasFeatureIdWriteRole(projectId, feature, featureId, roles)) {
            return true;
        }
        else if (projectId && feature && this.hasFeatureWriteRole(projectId, feature, roles)) {
            return true;
        }
        else if (projectId && this.hasProjectWriteRole(projectId, roles)) {
            return true;
        }
        else {
            return false;
        }
    }
    static hasProjectWriteRole(projectId, roles) {
        return Array.isArray(roles) && roles.includes(this.getProjectWriteRole(projectId));
    }
    /** Project admins or users with financials write can create/edit resources and financials data. */
    static canWriteFinancials(projectId, roles) {
        return this.hasProjectWriteRole(projectId, roles) || this.hasFeatureWriteRole(projectId, Feature.FINANCIALS, roles);
    }
    /** Project admins or users with any financials role (read or write) can view rates and financials data. */
    static canReadFinancials(projectId, roles) {
        return this.hasProjectWriteRole(projectId, roles) || this.hasFeatureReadRole(projectId, Feature.FINANCIALS, roles);
    }
    /**
     * Schedule feature read `schedule-read` and `schedule-write`.
     * Schedule-write is the parent of worklogs/estimates access.
     */
    static canReadSchedule(projectId, roles) {
        if (!Array.isArray(roles)) {
            return false;
        }
        return (this.hasProjectReadRole(projectId, roles) ||
            this.hasProjectWriteRole(projectId, roles) ||
            this.hasFeatureReadRole(projectId, Feature.SCHEDULES, roles) ||
            roles.includes(`p-${projectId}-schedules-read`) ||
            roles.includes(`p-${projectId}-schedules-write`));
    }
    /** Project admins or users with schedule write (canonical or legacy) can edit the schedule. */
    static canWriteSchedule(projectId, roles) {
        if (!Array.isArray(roles)) {
            return false;
        }
        return (this.hasProjectWriteRole(projectId, roles) ||
            this.hasFeatureWriteRole(projectId, Feature.SCHEDULES, roles) ||
            roles.includes(`p-${projectId}-schedules-write`));
    }
    /** Worklogs/estimates read: schedule roles (parent) or explicit schedule-worklogs / legacy worklogs roles. */
    static canReadScheduleWorklogs(projectId, roles) {
        if (!Array.isArray(roles)) {
            return false;
        }
        return (this.canReadSchedule(projectId, roles) ||
            this.hasFeatureReadRole(projectId, 'schedule-worklogs', roles));
    }
    /** True when `database` is the shared project resources DB (`p-{id}-resources`). */
    static isResourcesDatabase(database) {
        const projectId = this.getProjectIdFromDatabase(database);
        return Boolean(projectId && database === `p-${projectId}-resources`);
    }
    static getUsersWriteRole(projectId) {
        return `p-${projectId}-users-write`;
    }
    /** Project administrator (`p-{id}-write`) or user admin (`p-{id}-users-write`). */
    static canManageProjectUsers(projectId, roles) {
        return this.hasProjectWriteRole(projectId, roles) || this.hasFeatureWriteRole(projectId, 'users', roles);
    }
    static hasProjectReadRole(projectId, roles) {
        return Array.isArray(roles) && roles.includes(this.getProjectReadRole(projectId));
    }
    /** CouchDB proxy JWT roles: `p-{projectId}-read` or project write. */
    static hasProjectReadAccess(database, roles) {
        if (!Array.isArray(roles)) {
            return false;
        }
        const projectId = RoleUtils.getProjectIdFromDatabase(database);
        if (!projectId) {
            return false;
        }
        return this.hasProjectReadRole(projectId, roles) || this.hasProjectWriteRole(projectId, roles);
    }
    /** CouchDB proxy JWT roles: `p-{projectId}-read` or project write. */
    static hasProjectWriteAccess(database, roles) {
        if (!Array.isArray(roles)) {
            return false;
        }
        const projectId = RoleUtils.getProjectIdFromDatabase(database);
        if (!projectId) {
            return false;
        }
        return this.hasProjectWriteRole(projectId, roles);
    }
    static getProjectIdFromDatabase(database) {
        const match = database.match(RoleUtils.PROJECT_DB_NAME);
        return match ? match[1] : null;
    }
    static isValidProjectDatabase(database) {
        return Boolean(database.match(RoleUtils.PROJECT_DB_NAME));
    }
    /** CouchDB database-level read using the full database name (e.g. `p-123-files`). */
    static hasFeatureReadAccess(database, roles) {
        if (!this.isValidProjectDatabase(database) || !Array.isArray(roles)) {
            return false;
        }
        if (this.hasProjectReadAccess(database, roles)) {
            return true;
        }
        if (roles.includes(`${database}-read`) || this.hasFeatureWriteAccess(database, roles)) {
            return true;
        }
        if (this.isResourcesDatabase(database)) {
            const projectId = this.getProjectIdFromDatabase(database);
            if (!projectId) {
                return false;
            }
            if (this.canReadSchedule(projectId, roles)) {
                return true;
            }
            const impliedReadRoles = new RoleUtils(projectId).resourcesRoles;
            return roles.some((role) => impliedReadRoles.includes(role));
        }
        return false;
    }
    /** CouchDB database-level write using the full database name (e.g. `p-123-files`). */
    static hasFeatureWriteAccess(database, roles) {
        if (!this.isValidProjectDatabase(database) || !Array.isArray(roles)) {
            return false;
        }
        return roles.includes(`${database}-write`) || this.hasProjectWriteAccess(database, roles);
    }
    /** CouchDB document-level read using database name and document id. */
    static hasDocumentReadAccess(database, docId, roles) {
        if (!this.isValidProjectDatabase(database) || !Array.isArray(roles)) {
            return false;
        }
        if (this.hasFeatureReadAccess(database, roles)) {
            return true;
        }
        return roles.includes(`${database}-${docId}-read`) || this.hasDocumentWriteAccess(database, docId, roles);
    }
    /** CouchDB document-level write using database name and document id. */
    static hasDocumentWriteAccess(database, docId, roles) {
        if (this.hasFeatureWriteAccess(database, roles)) {
            return true;
        }
        return Array.isArray(roles) && roles.includes(`${database}-${docId}-write`);
    }
    /** True when the user has any doc-specific read or write role for the database. */
    static hasSomeDocumentReadAccess(database, roles) {
        if (!Array.isArray(roles)) {
            return false;
        }
        return roles.some((role) => role.startsWith(`${database}-`) && (role.endsWith('-read') || role.endsWith('-write')));
    }
    static hasFeatureOrSomeDocumentReadAccess(database, roles) {
        if (!Array.isArray(roles)) {
            return false;
        }
        return this.hasFeatureReadAccess(database, roles) || this.hasSomeDocumentReadAccess(database, roles);
    }
    /** True when the user has any doc-specific write role for the database. */
    static hasSomeDocumentWriteAccess(database, roles) {
        if (!Array.isArray(roles)) {
            return false;
        }
        return roles.some((role) => role.startsWith(`${database}-`) && role.endsWith('-write'));
    }
    static hasFeatureOrSomeDocumentWriteAccess(database, roles) {
        if (!Array.isArray(roles)) {
            return false;
        }
        return this.hasFeatureWriteAccess(database, roles) || this.hasSomeDocumentWriteAccess(database, roles);
    }
    static hasFeatureWriteRole(projectId, feature, roles) {
        return Array.isArray(roles) && roles.includes(`p-${projectId}-${feature}-write`);
    }
    static hasFeatureIdWriteRole(projectId, feature, featureId, roles) {
        return Array.isArray(roles) && roles.includes(`p-${projectId}-${feature}-${featureId}-write`);
    }
    static hasFeatureReadRole(projectId, feature, roles) {
        return (this.hasFeatureWriteRole(projectId, feature, roles) ||
            (Array.isArray(roles) && roles.includes(`p-${projectId}-${feature}-read`)));
    }
    static hasFeatureIdReadRole(projectId, feature, featureId, roles) {
        return (this.hasFeatureIdWriteRole(projectId, feature, featureId, roles) ||
            (Array.isArray(roles) && roles.includes(`p-${projectId}-${feature}-${featureId}-read`)));
    }
    static canRead(projectId, feature, featureId, roles) {
        if (this.canWrite(projectId, feature, featureId, roles)) {
            return true;
        }
        else if (projectId && feature && featureId && this.hasFeatureIdReadRole(projectId, feature, featureId, roles)) {
            return true;
        }
        else if (projectId && feature && this.hasFeatureReadRole(projectId, feature, roles)) {
            return true;
        }
        else if (projectId && this.hasProjectReadRole(projectId, roles)) {
            return true;
        }
        else {
            return false;
        }
    }
    static hasFilesWriteRole(projectId, roles) {
        if (RoleUtils.hasProjectWriteRole(projectId, roles)) {
            return true;
        }
        // Files can be uploaded from multiple features; allow feature-wide or doc-specific write on any of them.
        const features = [Feature.FILES, Feature.CONTRACTS, Feature.RFIS, Feature.SUBMITTALS, Feature.SCHEDULES];
        return features.some((feature) => this.hasFeatureOrSomeDocumentWriteAccess(`p-${projectId}-${feature}`, roles));
    }
    static getDocumentReadRole(database, docId) {
        return `${database}-${docId}-read`;
    }
    static getDocumentWriteRole(database, docId) {
        return `${database}-${docId}-write`;
    }
    static getFeatureRoles(projectId, feature) {
        return [`p-${projectId}-${feature}-read`, `p-${projectId}-${feature}-write`];
    }
    static getFeatureIdRoles(projectId, feature, featureId) {
        return [`p-${projectId}-${feature}-${featureId}-read`, `p-${projectId}-${feature}-${featureId}-write`];
    }
    /**
     * Exact overlap between JWT roles and a document's `roles` array.
     * Used by the CouchDB proxy when inspecting files/photos docs.
     */
    static userCanAccessDocRoles(userRoles, docRoles) {
        if (!Array.isArray(userRoles) || !Array.isArray(docRoles) || docRoles.length === 0) {
            return false;
        }
        return docRoles.some((docRole) => userRoles.includes(docRole));
    }
    /**
     * For certain databases, we inspect each doc to see if the user can read it.
     * This means the proxy downloads the entire database, and then inspects each doc.
     */
    static inspectDocumentReadAccess(userRoles, doc) {
        return this.userCanAccessDocRoles(userRoles, doc?.roles);
    }
    static inspectDocumentWriteAccess(userRoles, doc) {
        if (!this.userCanAccessDocRoles(userRoles, doc?.roles)) {
            return false;
        }
        return (doc.roles || []).some((docRole) => userRoles.includes(docRole) && docRole.endsWith('-write'));
    }
}
